import { Auth, Connect, Message, SocketObservable } from '@genesislcap/foundation-comms';
import { observable } from '@microsoft/fast-element';
import { DI } from '@microsoft/fast-foundation';
import { Documents, Project } from '../types';

export interface IProjectModalService {
  totalCompletedFields: any[];
  requiredFormFields: any[];
  modalMode: 'create' | 'edit';
  addToTotalCompletedFields(field: any);
  removeFromTotalCompletedFields(field: any);
  addToRequiredFormField(fields: any[]);
  removeFromRequiredFormField(fields: any[]);
  clearFields();
  eventProjectInsert(project: Project): Promise<any>;
  eventProjectUpdate(project: Project): Promise<any>;
  getDocument(projectID: string): Promise<Message<any>>;
  getDraftDocument(draftProjectId: string): Promise<Message<any>>;
  eventDocumentsInsert(projectID: string, documents: Documents): Promise<any>;
  eventDraftDocumentsUpsert(projectID: string, documents: Documents): Promise<any>;
  eventDraftProjectUpsert(draftProject: Project): Promise<any>;
  eventDraftProjectDelete(draftProject: Project): Promise<any>;
  getProjectNames(projectName: string, isLegal: boolean): Promise<Message<any>>;
  getRemodelOperationalProjects(
    iso: string,
    technology: string,
    isLinked: boolean
  ): Promise<Message<any>>;
}

class ProjectModalServiceImpl implements IProjectModalService {
  constructor(@Connect private connect: Connect, @Auth private auth: Auth) {}

  @observable public totalCompletedFields = [];
  @observable public requiredFormFields = [];
  @observable public modalMode: 'create' | 'edit' = 'create';

  public addToTotalCompletedFields(field) {
    if (!this.totalCompletedFields.includes(field)) {
      this.totalCompletedFields.push(field);
    }
  }

  public removeFromTotalCompletedFields(field) {
    const index = this.totalCompletedFields.indexOf(field);
    if (index > -1) {
      this.totalCompletedFields.splice(index, 1);
    }
  }

  public addToRequiredFormField(fields) {
    fields.forEach((field) => {
      if (!this.requiredFormFields.includes(field)) {
        this.requiredFormFields.push(field);
      }
    });
  }

  public removeFromRequiredFormField(fields: any[]) {
    fields.forEach((field) => {
      const index = this.requiredFormFields.indexOf(field);
      if (index > -1) {
        this.requiredFormFields.splice(index, 1);
      }
    });
  }

  public clearFields() {
    this.totalCompletedFields.splice(0);
    this.requiredFormFields.splice(0);
  }

  public async eventProjectInsert(project: Project): Promise<any> {
    if (!project) return;

    const request: any = await this.connect.commitEvent('EVENT_PROJECT_INSERT', {
      DETAILS: project,
      IGNORE_WARNINGS: true,
      VALIDATE: false,
    });

    return request;
  }

  public async eventProjectUpdate(project: Project): Promise<any> {
    if (!project) return;

    const request: any = await this.connect.commitEvent('EVENT_PROJECT_UPDATE', {
      DETAILS: project,
      IGNORE_WARNINGS: true,
      VALIDATE: false,
    });

    return request;
  }

  public async eventDraftProjectUpsert(draftProject: Project): Promise<any> {
    if (!draftProject) return;

    const request: any = await this.connect.commitEvent('EVENT_DRAFT_PROJECT_UPSERT', {
      DETAILS: draftProject,
      IGNORE_WARNINGS: true,
      VALIDATE: false,
    });

    return request;
  }

  public async eventDraftProjectDelete(draftProject: Project): Promise<any> {
    if (!draftProject || !draftProject.DRAFT_PROJECT_ID) return;

    const request: any = await this.connect.commitEvent('EVENT_DRAFT_PROJECT_DELETE', {
      DETAILS: draftProject,
      IGNORE_WARNINGS: true,
      VALIDATE: false,
    });

    return request;
  }

  public async eventDocumentsInsert(projectID: string, documents: Documents): Promise<any> {
    if (!documents) return;

    const request: any = await this.connect.commitEvent('EVENT_DOCUMENTS_STATUS_UPSERT', {
      DETAILS: {
        ...{ PROJECT_ID: projectID },
        ...documents,
      },
      IGNORE_WARNINGS: true,
      VALIDATE: false,
    });

    return request;
  }

  public async eventDraftDocumentsUpsert(
    draftProjectId: string,
    draftDocuments: Documents
  ): Promise<any> {
    if (!draftDocuments) return;

    const request: any = await this.connect.commitEvent('EVENT_DRAFT_DOCUMENTS_STATUS_UPSERT', {
      DETAILS: {
        ...{ DRAFT_PROJECT_ID: draftProjectId },
        ...draftDocuments,
      },
      IGNORE_WARNINGS: true,
      VALIDATE: false,
    });

    return request;
  }

  public getProjectNames(projectName: string, isLegal: boolean): Promise<Message<any>> {
    const criteria = isLegal ? `LEGAL_NAME == '${projectName}'` : `NAME == '${projectName}'`;
    const request = this.connect.snapshot('ALL_PROJECTS', { CRITERIA_MATCH: criteria });
    return request;
  }

  public getDocument(projectID: string): Promise<Message<any>> {
    return this.connect.request('DOCUMENTS', {
      REQUEST: {
        PROJECT_ID: projectID,
      },
    });
  }

  public getDraftDocument(draftProjectId: string): Promise<Message<any>> {
    return this.connect.request('DRAFT_DOCUMENTS', {
      REQUEST: {
        DRAFT_PROJECT_ID: draftProjectId,
      },
    });
  }

  getRemodelOperationalProjects(
    iso: string,
    technology: string,
    isLinked: boolean
  ): Promise<Message<any>> {
    return this.connect.request('REMODEL_OPERATIONAL_PROJECT', {
      REQUEST: {
        ISO_STRING: iso,
        TECHNOLOGY_STRING: technology,
        IS_LINKED: isLinked,
      },
    });
  }
}

export const IProjectModalService = DI.createInterface<IProjectModalService>((x) =>
  x.singleton(ProjectModalServiceImpl)
);
